import React, { useRef } from "react";
import { useEffect, useLayoutEffect, useState } from "react";
import useMobileBreakpoint from "@arctheme-hooks/useMobileBreakpoint";
import Tile from "@project-stories/elements/Tile/Tile";
import { Headings } from "@arctheme-components/elements/Headings/Headings";
import { Paragraph } from "@arctheme-components/elements/Paragraph/Paragraph";

export default function Grid({
     settings,
     columns,
     headerText,
     subtext,
     tagline,
     columns_count,
}) {
    const { section, mobile_breakpoint } = settings;
    const isMobile = useMobileBreakpoint(settings.mobile_breakpoint);

    const isFirstColumnDefaultLayout = columns[0]?.settings.layout === "default";
    let firstBlockHeight = isMobile
        ? columns[0]?.settings.mobile_block_height
        : columns[0]?.settings.block_height;

    if (!isFirstColumnDefaultLayout) {
        firstBlockHeight = 0;
    }
    const gapBetweenColumnsX = isMobile
        ? settings.section.gap_mobile
        : settings.section.gap;
    const gapBetweenColumnsY = isMobile
        ? settings.section.gap_y_mobile
        : settings.section.gap_y;

    const columnsCount = isMobile
        ? settings.section.columns_count_mobile
        : settings.section.columns_count;


    const titleStyle = isMobile
        ? settings.section.header_style_mobile
        : settings.section.header_style;

    const taglineStyle = isMobile
        ? settings.section.tagline_style_mobile
        : settings.section.tagline_style;

    const contentStyle = isMobile
        ? settings.section.subtext_style_mobile
        : settings.section.subtext_style;

    const layoutType = isMobile
        ? settings.section.layout_mobile
        : settings.section.layout;

    const gridColumnTiles: any = [];

    columns.map((columnData) => {
        // If column setting is 0, put it in its own group
        const columnSetting = columnData.settings.column || 0;
        
        if (columnSetting === 0) {
            // For columns with setting 0, create a unique index to ensure they're not grouped together
            const nextAvailableIndex = gridColumnTiles.length;
            gridColumnTiles[nextAvailableIndex] = [columnData];
        } else {
            // For columns with non-zero settings, group them by column setting
            if (!gridColumnTiles[columnSetting]) {
                gridColumnTiles[columnSetting] = [];
            }
            gridColumnTiles[columnSetting].push(columnData);
        }
    });

    return (
        <div className={`multicolumn-${layoutType}`}>
            {(tagline || headerText || subtext) && (
                <div className={`multicolumn-content-top w-full my-[16px]
                    justify-${settings.section.content_alignment_mobile}
                    lg:justify-${settings.section.content_alignment_desktop}
                    text-${settings.section.content_alignment_mobile}
                    lg:text-${settings.section.content_alignment_desktop}
                `}>
                    {tagline && <Paragraph text={tagline} type={taglineStyle}/>}
                    {headerText && <Headings text={headerText} type={`h2`} classes={titleStyle}/>}
                    {subtext && <Paragraph text={subtext} type={contentStyle} maxChars={settings.section.max_characters_subtext}/>}
                </div>
            )}
            {( gridColumnTiles.length > 0 && 
            <div
                className={`
                    ${layoutType === 'grid' ? 'grid' : 'flex'} 
                    ${layoutType === 'grid' ? `grid-cols-${columnsCount}` : ''} 
                    w-full 
                    gap-x-[${gapBetweenColumnsX}px] 
                    gap-y-[${gapBetweenColumnsY}px] 
                    ${firstBlockHeight > 0 ? `lg:h-[${firstBlockHeight}px]` : ''}  
                    ${settings.direction} 
                    justify-${settings.section.content_position_mobile} 
                    lg:justify-${settings.section.content_position_desktop}
                `}
                style={{
                    ...(layoutType === 'grid' && {
                        gridTemplateColumns: `repeat(${columnsCount}, 1fr)`
                    })
                }}
                {...(settings.section.use_carousel &&
                    !isMobile && {
                        "x-data": "carousel()",
                        "x-init": "$nextTick(() => { initialise(false, true, false) })",
                        "x-ref": "carousel",
                    })}
                {...(settings.section.use_carousel &&
                    isMobile && {
                        "x-data": "carousel()",
                        "x-init": "$nextTick(() => { destroy() })",
                        "x-ref": "carousel",
                    })}
            >
                {gridColumnTiles.map((groupedTiles, groupIndex) => {
                    // Calculate group-level width properties
                    let groupFlexBasis = '';
                    const defaultFlexBasis = `${100 / columns_count}%`;
                    
                    // Only set flex-basis for the primary layout groups
                    if (groupedTiles.length > 0) {
                        const columnSettings = groupedTiles[0].settings;
                        let columnWidth = isMobile
                            ? columnSettings.width_mobile
                            : columnSettings.width;
                        
                        if (layoutType !== 'grid') {
                            groupFlexBasis = columnWidth > 0
                                ? `${columnWidth}%`
                                : `${defaultFlexBasis}`;
                        }
                        
                        // Special handling for aspect ratio
                        if (columnSettings.main_aspect_ratio && layoutType !== 'grid') {
                            const aspectRatioWidth = isMobile
                                ? columnSettings.mobile_block_height
                                : columnSettings.block_height;
                                
                            if (aspectRatioWidth > 0) {
                                groupFlexBasis = `calc(${aspectRatioWidth}px - ${gapBetweenColumnsX}px)`;
                            }
                        }
                    }
                    
                    return (
                        <div 
                            key={groupIndex} 
                            className={`
                                ${(groupedTiles.length > 1 && groupIndex > 1) ? `flex flex-col justify-${groupedTiles[0].settings.column_vertical_position}` : ``}
                                ${(groupedTiles.length > 1 && groupIndex == 0) ? `flex flex-row flex-wrap` : ``}
                            `}
                            style={{
                                ...(layoutType !== 'grid' && groupFlexBasis && {
                                    flexBasis: groupFlexBasis
                                })
                            }}
                        >
                        {
                        groupedTiles.map((column, index) => {
                            // Calculate height for the column
                            const columnHeight = isMobile
                                ? column.settings.mobile_block_height
                                : column.settings.block_height;
                            const heightStyle = columnHeight > 0 ? `${columnHeight}px` : "auto";

                            return (
                                <div
                                    key={column.key || index}
                                    style={{
                                        height: column.settings.layout == "default" && column.settings.full_height_column === false && heightStyle,
                                        ...(settings.section.use_carousel && {
                                            marginLeft: `${gapBetweenColumnsX / 2}px`,
                                            marginRight: `${gapBetweenColumnsX / 2}px`,
                                        }),
                                    }}
                                    className={`box-border overflow-hidden ${column.settings.image && column.settings.image_shadow ? "tile__shadow" : '' } ${columnHeight === 0 && column.settings.full_height_column === true ? "h-full" : ""}`}
                                >
                                    <Tile
                                        data={column}
                                        layout={column.settings.layout}
                                        height={heightStyle}
                                        settings={settings}
                                    />
                                </div>
                            );
                        })
                    }
                    </div>
                    );
                })}
            </div>
            )}
        </div>
    );
}
